import { PartnersCapitalCategory, Schema } from 'further-types/lp/account';
import { z } from 'zod';
import { isMongoId } from '../../helpers';

export const ListAll = z.object({
  searchQuery: z.string().optional().nullable(),
});

const accountBaseFields = z.object({
  section: z.nativeEnum(Schema.AccountSection),
  accountName: z.string().min(1, 'Please enter account name.'),
  accountCode: z.string().min(1, 'Please enter account code.'),
  payableReceivableCategory: z
    .nativeEnum(Schema.PayableReceivableCategory)
    .nullable()
    .optional(),
  partnersCapitalCategory: z
    .nativeEnum(PartnersCapitalCategory)
    .nullable()
    .optional(),
  balanceSheetSection: z.nativeEnum(Schema.BalanceSheetSection).optional(),
  incomeExpenseCategory: z.string().trim().nullable().optional(),
  customActionTypeId: z.string().nullable().optional(),
});

type AccountBaseInferred = z.infer<typeof accountBaseFields>;

const balanceSheetSectionRefinementIssue = {
  message:
    'Balance sheet section is required for Assets and Payables accounts.',
  path: ['balanceSheetSection'],
};

const payableReceivableCategoryRefinementIssue = {
  message: 'Payable/receivable category is required for Payables section',
  path: ['payableReceivableCategory'],
};

const incomeExpenseCategoryRefinementIssue = {
  message: 'Income/expense category is required for Income/Expenses section',
  path: ['incomeExpenseCategory'],
};

function meetsBalanceSheetSectionRules(
  data: AccountBaseInferred & { id?: string },
): boolean {
  if (
    data.section === Schema.AccountSection.Assets ||
    data.section === Schema.AccountSection.Payables
  ) {
    return !!data.balanceSheetSection;
  }
  return true;
}

function meetsPayableReceivableCategoryRules(
  data: AccountBaseInferred & { id?: string },
): boolean {
  if (data.section === Schema.AccountSection.Payables) {
    return !!data.payableReceivableCategory;
  }
  return true;
}

function meetsIncomeExpenseCategoryRules(
  data: AccountBaseInferred & { id?: string },
): boolean {
  if (
    data.section === Schema.AccountSection.Income ||
    data.section === Schema.AccountSection.Expenses
  ) {
    return !!data.incomeExpenseCategory;
  }
  return true;
}

export const CreateAccount = accountBaseFields
  .refine(meetsBalanceSheetSectionRules, balanceSheetSectionRefinementIssue)
  .refine(
    meetsPayableReceivableCategoryRules,
    payableReceivableCategoryRefinementIssue,
  )
  .refine(
    meetsIncomeExpenseCategoryRules,
    incomeExpenseCategoryRefinementIssue,
  );

export const UpdateAccount = accountBaseFields
  .extend({ id: z.string().refine(isMongoId()) })
  .refine(meetsBalanceSheetSectionRules, balanceSheetSectionRefinementIssue)
  .refine(
    meetsPayableReceivableCategoryRules,
    payableReceivableCategoryRefinementIssue,
  )
  .refine(
    meetsIncomeExpenseCategoryRules,
    incomeExpenseCategoryRefinementIssue,
  );

export const DeleteAccount = z.object({
  id: z.string().refine(isMongoId()),
});
